import { ScrollText, Phone } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { PAGE_META } from "@/data/page_meta";

import { CONTACT, BUSINESS_NAME } from "@/data/site";

const TERMS = [
  {
    title: "Cancel or reschedule",
    body: "Free with 48 hours' notice. Between 24 and 48 hours: $50 fee. Less than 24 hours: 50% of the cleaning price.",
  },
  {
    title: "Lockouts",
    body: "If our team can't get in (no key, wrong code, or no one home), we charge 50% of the cleaning price to cover the team's time and travel.",
  },
  {
    title: "Payment",
    body: "Your card or bank account is saved when you book. We charge it after each clean is done.",
  },
  {
    title: "The CJB Promise (every clean)",
    body: "24-hour redo guarantee. If we ever miss a detail, tell us within 24 hours and we'll return to make it right at no cost.",
  },
  {
    title: "Damage",
    body: "We're fully insured. Please tell us about any damage within 24 hours. We're not responsible for items that were already broken or loose, or for valuables left out, so please put away cash, jewelry, and fragile keepsakes.",
  },
  {
    title: "Safety",
    body: "Please secure pets. Our team may skip areas that aren't safe, like mold, pests, bodily fluids, or anything that needs a tall ladder.",
  },
  {
    title: "What's not included",
    body: "Moving heavy furniture, outside windows, and biohazard cleanup.",
  },
  {
    title: "Recurring Promise laundry",
    body: "One load of laundry, washed and dried. Folded when the dryer finishes before we leave.",
    fine: "One load per visit, using your detergent. We start it when we arrive. If the dryer is still running when we finish, we'll leave it to finish drying. Machine-washable only. No delicates or dry-clean-only items. We're not responsible for damage to laundered items.",
  },
  {
    title: "Price changes",
    body: "Recurring prices are locked for 12 months. We may adjust a price if your home's size or condition is different from what was booked.",
  },
  {
    title: "Hiring our team",
    body: "Our team members agree not to take private work from CJB Clean clients, so hiring them directly would put them in breach of their agreement with us. If you'd like the same cleaner every time, just ask and we'll do our best to schedule them for you. If a client chooses to hire a current or former team member privately, that work is between the client and that person. CJB Clean's insurance, background screening, and guarantee no longer apply, and CJB Clean is not responsible for any damage, loss, or actions during that private work.",
  },
];

export default function ServiceTermsPage() {
  useSEO(PAGE_META.serviceTerms);

  return (
    <div className="min-h-screen bg-muted/30 pt-32 pb-24" data-testid="service-terms-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <span className="w-14 h-14 rounded-full bg-leaf/15 flex items-center justify-center mb-6">
          <ScrollText className="w-7 h-7 text-leaf" />
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-navy">Service Terms</h1>
        <p className="mt-5 text-muted-foreground">
          These are the terms for every clean booked with {BUSINESS_NAME}. Booking a service means you agree to them.
        </p>

        <ol className="mt-10 space-y-6">
          {TERMS.map((t, i) => (
            <li key={t.title} className="rounded-2xl border border-border bg-white p-6" data-testid={`term-${i + 1}`}>
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-8 h-8 rounded-full bg-leaf/15 text-leaf font-heading font-bold flex items-center justify-center">{i + 1}</span>
                <div>
                  <h2 className="font-heading text-lg font-semibold text-navy">{t.title}</h2>
                  <p className="mt-2 text-muted-foreground leading-relaxed">{t.body}</p>
                  {t.fine && (
                    <p className="mt-3 text-sm text-muted-foreground/80 italic border-l-2 border-leaf/30 pl-3">{t.fine}</p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-10 rounded-2xl border border-border bg-white p-5 flex items-center gap-3">
          <Phone className="w-5 h-5 text-leaf" />
          <p className="text-sm text-navy">Questions about our terms? Call us at <a href={CONTACT.phoneHref} className="text-leaf font-medium">{CONTACT.phone}</a>.</p>
        </div>
      </div>
    </div>
  );
}
